'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { gatedAction } from '@/lib/api/auth-gate';
import { accountKeys } from '@/lib/query-keys';
import {
  getPaymentMethodsAction,
  removePaymentMethodAction,
  setDefaultPaymentMethodAction
} from './actions';
import type { PaymentMethodsResponse } from './types';

/**
 * Hook reading the firm's saved cards from GET /account/billing/payment-methods
 * (LF-4605). Short cache — cards change only when the firm adds/removes one, and
 * those mutations invalidate this key so the list refreshes immediately.
 */
export function usePaymentMethods() {
  return useQuery<PaymentMethodsResponse>({
    queryKey: accountKeys.billingPaymentMethods,
    queryFn: () => gatedAction(() => getPaymentMethodsAction()),
    staleTime: 60 * 1000
  });
}

/**
 * Remove a saved card. Unwraps the ActionResult so a BE guard failure (e.g.
 * "can't remove your only card") rejects with its message for the caller to
 * toast. Invalidates the list on success.
 */
export function useRemovePaymentMethod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (paymentMethodId: string) => {
      const result = await removePaymentMethodAction(paymentMethodId);
      if (!result.success) throw new Error(result.error);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: accountKeys.billingPaymentMethods
      })
  });
}

/** Set a saved card as the invoice default; invalidates the list on success. */
export function useSetDefaultPaymentMethod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (paymentMethodId: string) => {
      const result = await setDefaultPaymentMethodAction(paymentMethodId);
      if (!result.success) throw new Error(result.error);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: accountKeys.billingPaymentMethods
      })
  });
}
